import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { CancelAdvanceBookingAssignLockers, EarlyCheckInAdvance } from "../api/api";
import { BUTTON_PRIMARY, BUTTON_WARNING, TOAST_ERROR, TOAST_SUCCESS } from "../assets/constants/Constants";
import { capitalizeWords, getPathForNextPage, getReservationStartDate, isCancellationAllowed, isModificationAllowed } from "../lib/Utils";
import CustomDialog from "./CustomDialog";
import ButtonComponent from "./ButtonComponent";
import { useTranslation, Trans } from "react-i18next";
import { MAP, USER_ACCOUNT } from "../assets/constants/PageList";
import CircularProgressLoader from "./CircularProgressLoader";
import ShowToast from "./ToastComponent";

export default function UpcomingLockersComponent({ locker, onBookingUpdated }) {
  const EARLY_CHECK_IN_WINDOW_HOURS = 3;
  const EARLY_CHECK_IN_WINDOW_MS = EARLY_CHECK_IN_WINDOW_HOURS * 60 * 60 * 1000;
  const history = useHistory();
  const { t } = useTranslation();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isBlockedDialogOpen, setIsBlockedDialogOpen] = useState(false);
  const [isModifyDialogOpen, setIsModifyDialogOpen] = useState(false);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(t("pleaseWaitText"));
  const [blockedDialogTitle, setBlockedDialogTitle] = useState(t("cancellation_not_allowed_title"));
  const [blockedDialogMessage, setBlockedDialogMessage] = useState(
    t("cancellation_not_allowed_within_hours_text", { hours: 3 })
  );

  const getTranslatedFallback = (fallbackKey, fallbackText) => {
    const translatedValue = t(fallbackKey);
    return translatedValue === fallbackKey ? fallbackText : translatedValue;
  };

  const getSuccessMessage = (response, fallbackKey, fallbackText) =>
    response?.data?.message
      ? t(response.data.message)
      : getTranslatedFallback(fallbackKey, fallbackText);

  const getErrorMessage = (error, fallbackKey, fallbackText) =>
    error?.response?.data?.message
      ? t(error.response.data.message)
      : getTranslatedFallback(fallbackKey, fallbackText);

  const formatTimeToAmPm = (timeValue) => {
    if (!timeValue || typeof timeValue !== "string") return timeValue;

    const normalizedTime = timeValue.trim().toUpperCase();
    if (normalizedTime.includes("AM") || normalizedTime.includes("PM")) {
      return normalizedTime;
    }

    const [hoursString, minutesString = "00"] = normalizedTime.split(":");
    const hours = Number(hoursString);
    const minutes = Number(minutesString);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return timeValue;
    }

    const suffix = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    const formattedMinutes = String(minutes).padStart(2, "0");

    return `${formattedHours}:${formattedMinutes} ${suffix}`;
  };

  const HandleLockerCancel = (lockerItem) => {
    setSelectedLocker(lockerItem);
    setIsCancelDialogOpen(true);
  };

  const HandleLockerModication = (lockerItem) => {
    setSelectedLocker(lockerItem);
    setIsModifyDialogOpen(true);
  };

  const confirmModifyBooking = async () => {
    try {
      const reserveStart = getReservationStartDate(selectedLocker);
      if (!reserveStart) return;
      

      if (!isModificationAllowed(reserveStart, 3)) {
        setIsModifyDialogOpen(false);
        setBlockedDialogTitle(t("modification_not_allowed_title"));
        setBlockedDialogMessage(t("modification_not_allowed_within_hours_text", { hours: 3 }));
        setIsBlockedDialogOpen(true);
        return;
      }

      setIsModifyDialogOpen(false);
      setLoading(true);
      setLoadingMessage(t("pleaseWaitText"));
      const requestdata = {
        transaction_id: selectedLocker.ref_id,
        cancellation_reason: "Cancelled by user for modification",
      };

      const response = await CancelAdvanceBookingAssignLockers(requestdata);
      ShowToast(
        getSuccessMessage(response, "modify_booking_success_text", "Booking modified successfully."),
        TOAST_SUCCESS
      );
      history.push(getPathForNextPage(USER_ACCOUNT, MAP));
    } catch (error) {
      console.error(error);
      ShowToast(
        getErrorMessage(error, "something_went_wrong_text", "Something went wrong."),
        TOAST_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmCancelBooking = async () => {
    try {
      const reserveStart = getReservationStartDate(selectedLocker);
      if (!reserveStart) return;

      if (!isCancellationAllowed(reserveStart, 3)) {
        setIsCancelDialogOpen(false);
        setBlockedDialogTitle(t("cancellation_not_allowed_title"));
        setBlockedDialogMessage(t("cancellation_not_allowed_within_hours_text", { hours: 3 }));
        setIsBlockedDialogOpen(true);
        return;
      }
      setIsCancelDialogOpen(false);
      setLoading(true);
      setLoadingMessage(t("pleaseWaitText"));

      const requestdata = {
        transaction_id: selectedLocker.ref_id,
        cancellation_reason: "Cancelled by user",
      };

      const response = await CancelAdvanceBookingAssignLockers(requestdata);

      ShowToast(
        getSuccessMessage(response, "cancel_booking_success_text", "Booking cancelled successfully."),
        TOAST_SUCCESS
      );
      await onBookingUpdated?.();
    } catch (error) {
      console.error(error);
      ShowToast(
        getErrorMessage(error, "something_went_wrong_text", "Something went wrong."),
        TOAST_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const CancelBookingContent = ({ locker }) => (
    <div>
      <h3>{t("cancel_booking_title")}</h3>
      <p>
        <Trans
          i18nKey="cancel_booking_confirm_text"
          values={{ lockerNumber: locker?.lockerNumber }}
          components={{ strong: <strong /> }}
        />
      </p>
    </div>
  );

  const ModifyBookingContent = ({ locker }) => (
    <div>
      <h3>{t("modify_booking_title")}</h3>
      <p>
        <Trans
          i18nKey="modify_booking_description_text"
          values={{ lockerNumber: locker?.lockerNumber }}
          components={{ strong: <strong /> }}
        />
      </p>
    </div>
  );

  const BlockedContent = () => (
    <div>
      <h3>{blockedDialogTitle}</h3>
      <p>{blockedDialogMessage}</p>
    </div>
  );

  const EarlyCheckInHandler = async (lockerItem) => {
    try {
      const reserveStart = getReservationStartDate(lockerItem);
      if (!reserveStart || !lockerItem?.ref_id) {
        ShowToast(
          getTranslatedFallback("Something went wrong."),
          TOAST_ERROR
        );
        return;
      }

      const isWithinEarlyCheckInWindow =
        reserveStart.getTime() - Date.now() <= EARLY_CHECK_IN_WINDOW_MS;

      if (!isWithinEarlyCheckInWindow) {
        setBlockedDialogTitle(
          getTranslatedFallback("Check-in not allowed")
        );
        setBlockedDialogMessage(
          getTranslatedFallback(
            `Check-in is available only within ${EARLY_CHECK_IN_WINDOW_HOURS} hours of check-in time.`
          )
        );
        setIsBlockedDialogOpen(true);
        return;
      }

      setLoading(true);
      setLoadingMessage(t("pleaseWaitText"));

      const response = await EarlyCheckInAdvance({
        transaction_id: lockerItem.ref_id,
        ref_id: lockerItem.ref_id,
      });

      ShowToast("Checked in successfully.",TOAST_SUCCESS);
      await onBookingUpdated?.();
    } catch (error) {
      console.error(error);
      ShowToast(
        getErrorMessage(error, "Something went wrong."),
        TOAST_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const reserveStart = getReservationStartDate(locker);
  const showCheckInNowButton =
    !!reserveStart && reserveStart.getTime() - Date.now() <= EARLY_CHECK_IN_WINDOW_MS;
  const showModifyBookingButton = !showCheckInNowButton;

  

  return (
    <>
      {loading && <CircularProgressLoader message={loadingMessage} />}

      <div className="locker-card upcoming">
        <div className="top">
          <div className="title">{locker.name}</div>
          <div className={`status ${locker.status}`}>
            {capitalizeWords(locker.status)}
          </div>
        </div>

        <div className="sub-text">
          {t("locker_number_and_size_text", {
            lockerNumber: locker.locker_bank_name,
            size: locker.size,
          })}
        </div>

        <div className="date-section">
          <div className="date-box">
              <span className="label">{t("check_in_label")}</span>
              <span className="value">{locker.checkInDate}</span>
              <span className="value">{formatTimeToAmPm(locker.checkInTime)}</span>
          </div>

          <div className="date-box">
            <span className="label">{t("check_out_label")}</span>
            <span className="value">{locker.checkOutDate}</span>
            <span className="value">{formatTimeToAmPm(locker.checkOutTime)}</span>
          </div>
        </div>

        <hr />
        {locker.status === "reserved" &&
        <div className="button-container">
          {showModifyBookingButton && (
            <ButtonComponent
              type={BUTTON_PRIMARY}
              handleClick={() => HandleLockerModication(locker)}
              title="modify_booking_button"
              disabled={loading}
            />
          )}

          {showCheckInNowButton && (
            <ButtonComponent
              type={BUTTON_PRIMARY}
              handleClick={() => EarlyCheckInHandler(locker)}
              title="check_in_now_button"
              disabled={loading}
            />
          )}
          <ButtonComponent
            type={BUTTON_WARNING}
            handleClick={() => HandleLockerCancel(locker)}
            title="cancel_booking_button"
            disabled={loading}
          />
        </div>
        }
      </div>

      <CustomDialog
        dialogVisible={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        handleCancel={() => setIsCancelDialogOpen(false)}
        handleAccept={confirmCancelBooking}
        buttonOneTitle="noText"
        buttonTwoTitle={loading ? "cancelling_text" : "yes_cancel_text"}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY },
        }}
      >
        {() => <CancelBookingContent locker={selectedLocker} />}
      </CustomDialog>

      <CustomDialog
        dialogVisible={isModifyDialogOpen}
        onClose={() => setIsModifyDialogOpen(false)}
        handleCancel={() => setIsModifyDialogOpen(false)}
        handleAccept={confirmModifyBooking}
        buttonOneTitle="noText"
        buttonTwoTitle={loading ? "pleaseWaitText" : "yes_modify_text"}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY },
        }}
      >
        {() => <ModifyBookingContent locker={selectedLocker} />}
      </CustomDialog>

      <CustomDialog
        dialogVisible={isBlockedDialogOpen}
        onClose={() => setIsBlockedDialogOpen(false)}
        handleCancel={() => setIsBlockedDialogOpen(false)}
        buttonOneTitle="button_ok"
        buttonProps={{
          buttonOne: { type: BUTTON_PRIMARY },
        }}
      >
        {() => <BlockedContent />}
      </CustomDialog>
    </>
  );
}
